import React, { useState, useEffect, useCallback } from 'react';
import {
  GxZone,
  CreateZoneData,
  UpdateZoneData,
  getZones,
  createZone,
  updateZone,
  deleteZone,
} from '../../api/guestExperience';

interface ZoneManagerProps {
  eventId: string;
}

const styles: Record<string, React.CSSProperties> = {
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  zoneRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
  },
  colorBadge: {
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    flexShrink: 0,
  },
  zoneInfo: {
    flex: 1,
    minWidth: 0,
  },
  zoneName: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#0f2d1f',
  },
  zoneDesc: {
    fontSize: '0.8rem',
    color: '#666',
    marginTop: '0.15rem',
  },
  statusBadge: {
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '0.15rem 0.5rem',
    borderRadius: '10px',
  },
  activeStatus: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  inactiveStatus: {
    backgroundColor: '#fdecea',
    color: '#d32f2f',
  },
  actionBtn: {
    padding: '0.3rem 0.5rem',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  addButton: {
    padding: '0.6rem 1rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.75rem',
  },
  modal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '400px',
  },
  modalTitle: {
    color: '#1a6b3c',
    marginBottom: '1rem',
    fontSize: '1.1rem',
    margin: '0 0 1rem 0',
  },
  field: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  },
  colorInput: {
    width: '60px',
    height: '36px',
    padding: '2px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  saveBtn: {
    width: '100%',
    padding: '0.7rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  cancelBtn: {
    width: '100%',
    padding: '0.7rem',
    backgroundColor: '#eee',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  empty: {
    color: '#999',
    fontSize: '0.85rem',
    fontStyle: 'italic',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '0.75rem',
    fontSize: '0.8rem',
  },
};

interface ZoneFormData {
  name: string;
  color: string;
  description: string;
}

const emptyForm: ZoneFormData = { name: '', color: '#1a6b3c', description: '' };

export function ZoneManager({ eventId }: ZoneManagerProps) {
  const [zones, setZones] = useState<GxZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingZone, setEditingZone] = useState<GxZone | null>(null);
  const [form, setForm] = useState<ZoneFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchZones = useCallback(async () => {
    try {
      const data = await getZones(eventId);
      setZones(data);
    } catch {
      setZones([]);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    setLoading(true);
    fetchZones();
  }, [fetchZones]);

  function openAdd() {
    setForm(emptyForm);
    setEditingZone(null);
    setError('');
    setShowModal(true);
  }

  function openEdit(zone: GxZone) {
    setForm({ name: zone.name, color: zone.color, description: zone.description || '' });
    setEditingZone(zone);
    setError('');
    setShowModal(true);
  }

  async function handleDelete(zone: GxZone) {
    if (!confirm(`Deactivate zone "${zone.name}"?`)) return;
    try {
      await deleteZone(eventId, zone.id);
      fetchZones();
    } catch {
      alert('Failed to deactivate zone.');
    }
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingZone) {
        const data: UpdateZoneData = {
          name: form.name.trim(),
          color: form.color,
          description: form.description.trim() || undefined,
        };
        await updateZone(eventId, editingZone.id, data);
      } else {
        const data: CreateZoneData = {
          name: form.name.trim(),
          color: form.color,
          description: form.description.trim() || undefined,
        };
        await createZone(eventId, data);
      }
      setShowModal(false);
      fetchZones();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to save zone.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p style={{ color: '#666', fontSize: '0.85rem' }}>Loading zones...</p>;
  }

  return (
    <div>
      {zones.length === 0 ? (
        <p style={styles.empty}>No zones configured for this event.</p>
      ) : (
        <div style={styles.list}>
          {zones.map((zone) => (
            <div key={zone.id} style={styles.zoneRow}>
              <div style={{ ...styles.colorBadge, backgroundColor: zone.color }} />
              <div style={styles.zoneInfo}>
                <div style={styles.zoneName}>{zone.name}</div>
                {zone.description && <div style={styles.zoneDesc}>{zone.description}</div>}
              </div>
              <span
                style={{
                  ...styles.statusBadge,
                  ...(zone.active ? styles.activeStatus : styles.inactiveStatus),
                }}
              >
                {zone.active ? 'Active' : 'Inactive'}
              </span>
              <button style={styles.actionBtn} onClick={() => openEdit(zone)} title="Edit">✏️</button>
              <button style={styles.actionBtn} onClick={() => handleDelete(zone)} title="Deactivate">🗑️</button>
            </div>
          ))}
        </div>
      )}

      <button style={styles.addButton} onClick={openAdd}>+ Add Zone</button>

      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>{editingZone ? 'Edit Zone' : 'Add Zone'}</h3>
            {error && <div style={styles.error}>{error}</div>}
            <div style={styles.field}>
              <label style={styles.label}>Name</label>
              <input
                style={styles.input}
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. VIP Lounge"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Color</label>
              <input
                style={styles.colorInput}
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Description</label>
              <input
                style={styles.input}
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
